/**
 * Intake Generator
 * 
 * Generates targeted or full intake forms based on missing data fields
 */

import { TherapyCategory, MissingField, SuggestedQuestion } from '../dataCompleteness/types';
import {
  IntakeTemplate,
  IntakeSection,
  IntakeQuestion,
  IntakeTemplateType,
  getIntakeTemplate,
  therapyCategoryToTemplateType,
  MENS_HEALTH_TEMPLATE,
  WOMENS_HEALTH_TEMPLATE,
  METABOLIC_TEMPLATE,
  PEPTIDE_HEALING_TEMPLATE,
} from './templates';

export interface GeneratedIntakeForm {
  id: string;
  type: 'targeted' | 'full';
  title: string;
  description: string;
  estimatedTime: string;
  sections: IntakeSection[];
  sourceTemplate: IntakeTemplateType;
  missingFieldsAddressed: string[];
}

/**
 * Generate a targeted intake form based on missing fields
 */
export function generateTargetedIntake(
  missingFields: MissingField[],
  therapyCategory: TherapyCategory,
  gender?: string | number
): GeneratedIntakeForm {
  const templateType = therapyCategoryToTemplateType(therapyCategory, gender);
  const sourceTemplate = getIntakeTemplate(templateType);
  
  // Map missing fields to questions
  const relevantQuestions: IntakeQuestion[] = [];
  const missingFieldIds = new Set(missingFields.map(f => f.field.id));
  const addressedFields: string[] = [];

  // Find questions that address missing fields
  for (const section of sourceTemplate.sections) {
    for (const question of section.questions) {
      // Check if this question addresses a missing field
      const addressesField = missingFields.some(mf => {
        const fieldId = mf.field.id.toLowerCase();
        const questionId = question.id.toLowerCase();
        const questionText = question.text.toLowerCase();
        
        return (
          questionId.includes(fieldId) ||
          fieldId.includes(questionId.split('_')[0]) ||
          questionText.includes(mf.field.name.toLowerCase())
        );
      });

      if (addressesField) {
        relevantQuestions.push(question);
        // Track which fields are addressed
        missingFields.forEach(mf => {
          const fieldId = mf.field.id.toLowerCase();
          const questionId = question.id.toLowerCase();
          if (questionId.includes(fieldId) || fieldId.includes(questionId.split('_')[0])) {
            addressedFields.push(mf.field.id);
          }
        });
      }
    }
  }

  // If we found relevant questions, create a targeted form
  if (relevantQuestions.length > 0) {
    // Group questions by category
    const groupedQuestions = groupQuestionsByCategory(relevantQuestions);
    
    const sections: IntakeSection[] = Object.entries(groupedQuestions).map(([category, questions]) => ({
      id: `targeted_${category}`,
      title: formatCategoryTitle(category),
      description: `Questions related to ${formatCategoryTitle(category).toLowerCase()}`,
      questions
    }));

    return {
      id: `targeted_${therapyCategory}_${Date.now()}`,
      type: 'targeted',
      title: `Additional Information Needed`,
      description: `Please answer these ${relevantQuestions.length} questions to complete your assessment.`,
      estimatedTime: `${Math.max(2, Math.ceil(relevantQuestions.length / 3))} minutes`,
      sections,
      sourceTemplate: templateType,
      missingFieldsAddressed: [...new Set(addressedFields)]
    };
  }

  // Fallback: Generate minimal intake from missing fields
  return generateMinimalIntake(missingFields, therapyCategory);
}

/**
 * Generate a full intake form for the therapy category
 */
export function generateFullIntake(
  therapyCategory: TherapyCategory,
  gender?: string | number
): GeneratedIntakeForm {
  const templateType = therapyCategoryToTemplateType(therapyCategory, gender);
  const template = getIntakeTemplate(templateType);

  return {
    id: `full_${therapyCategory}_${Date.now()}`,
    type: 'full',
    title: template.name,
    description: template.description,
    estimatedTime: template.estimatedTime,
    sections: template.sections,
    sourceTemplate: templateType,
    missingFieldsAddressed: [] // Full intake addresses everything
  };
}

/**
 * Generate a minimal intake form directly from missing fields
 */
function generateMinimalIntake(
  missingFields: MissingField[],
  therapyCategory: TherapyCategory
): GeneratedIntakeForm {
  const questions: IntakeQuestion[] = missingFields.map(mf => ({
    id: mf.field.id,
    text: getQuestionTextForField(mf.field.id, mf.field.name),
    inputType: getInputTypeForField(mf.field.category),
    required: mf.field.priority === 'required',
    helpText: mf.reason
  }));

  return {
    id: `minimal_${therapyCategory}_${Date.now()}`,
    type: 'targeted',
    title: 'Additional Information Needed',
    description: `Please provide the following ${questions.length} items to complete your assessment.`,
    estimatedTime: `${Math.max(2, Math.ceil(questions.length / 2))} minutes`,
    sections: [{
      id: 'missing_data',
      title: 'Required Information',
      questions
    }],
    sourceTemplate: therapyCategoryToTemplateType(therapyCategory),
    missingFieldsAddressed: missingFields.map(mf => mf.field.id)
  };
}

/**
 * Group questions by their category
 */
function groupQuestionsByCategory(questions: IntakeQuestion[]): Record<string, IntakeQuestion[]> {
  const grouped: Record<string, IntakeQuestion[]> = {};
  
  for (const question of questions) {
    const category = question.category || 'general';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(question);
  }
  
  return grouped;
}

/**
 * Format category name for display
 */
function formatCategoryTitle(category: string): string {
  const titles: Record<string, string> = {
    adam: 'ADAM Questionnaire',
    stop_bang: 'Sleep Apnea Screening',
    vasomotor: 'Vasomotor Symptoms',
    mood: 'Mood & Cognitive',
    physical: 'Physical Symptoms',
    reproductive: 'Reproductive Health',
    sleep: 'Sleep Quality',
    medical: 'Medical History',
    lifestyle: 'Lifestyle',
    goals: 'Treatment Goals',
    general: 'General Information'
  };
  
  return titles[category] || category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ');
}

/**
 * Get appropriate question text for a field ID
 */
function getQuestionTextForField(fieldId: string, fieldName: string): string {
  const questionMappings: Record<string, string> = {
    symptoms_assessment: 'Please describe your primary symptoms and concerns:',
    menopausal_status: 'What is your current menopausal status?',
    stop_bang: 'Sleep Apnea Screening: Do you snore loudly or feel tired during the day?',
    medical_history: 'Please list any significant medical conditions:',
    mtc_men2_screening: 'Do you have any personal or family history of thyroid cancer (MTC) or MEN2 syndrome?',
    weight: 'What is your current weight?',
    primary_indication: 'What is the primary condition you want to address?',
    athlete_status: 'Are you a competitive athlete subject to drug testing?'
  };

  return questionMappings[fieldId] || `Please provide information about: ${fieldName}`;
}

/**
 * Get appropriate input type for a field category
 */
function getInputTypeForField(category: string): 'boolean' | 'text' | 'number' | 'select' {
  switch (category) {
    case 'demographics':
      return 'text';
    case 'labs':
      return 'text';
    case 'vitals':
      return 'number';
    case 'symptoms':
    case 'questionnaire':
      return 'boolean';
    case 'medical_history':
      return 'text';
    default:
      return 'text';
  }
}

/**
 * Convert SuggestedQuestions to IntakeQuestions
 */
export function suggestedQuestionsToIntakeQuestions(
  suggestedQuestions: SuggestedQuestion[]
): IntakeQuestion[] {
  return suggestedQuestions.map(sq => ({
    id: sq.id,
    text: sq.text,
    inputType: sq.inputType,
    options: sq.options,
    required: sq.priority === 'required'
  }));
}

/**
 * Get the appropriate full template for a therapy category
 */
export function getTemplateForTherapy(therapyCategory: TherapyCategory): IntakeTemplate {
  switch (therapyCategory) {
    case 'trt':
      return MENS_HEALTH_TEMPLATE;
    case 'hrt':
      return WOMENS_HEALTH_TEMPLATE;
    case 'glp1':
      return METABOLIC_TEMPLATE;
    case 'peptide':
      return PEPTIDE_HEALING_TEMPLATE;
    default:
      return MENS_HEALTH_TEMPLATE;
  }
}
